import { RESULT_HEADERS, resultRows, type ResultsSource } from './resultsFile.js';

/**
 * Story #79 — the same results as the CSV (#47), for a reader who will open a
 * workbook and not a text file.
 *
 * Built client-side from the payload the page is already holding, for the
 * reason the CSV states: the server has nothing to add that the results view
 * is not already showing, and a route that re-derived it would be a second
 * answer to the same question, free to disagree with the first.
 *
 * The escaping deliberately does not come along. `FORMULA_STARTERS`, the
 * leading apostrophe and the `BOM` exist because a CSV is a text file Excel
 * guesses at; a sheet holds typed cells, so writing `'=1+1` into one corrupts
 * an author's option text instead of defending it — the cell below is typed as
 * a string, and a string cell is never evaluated.
 */
export const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * The one column the CSV does not have.
 *
 * `QuestionResult.multiSelect` is set when a device could pick several
 * options, which is when the percentages can sum past 100. The CSV's columns
 * are fixed by #47 and stay that way; a workbook is where a reader does
 * arithmetic on the numbers, so it is the format that has to say so.
 */
const MULTI_SELECT_HEADER = 'Multiple picks';

/**
 * Blank rather than "No" on an ordinary question: every row of a normal poll
 * would otherwise carry a column of noise to mark the case that is not there.
 */
const MULTI_SELECT_YES = 'Yes';

/** Wide enough for the text columns; the rest keep the writer's default. */
const COLUMNS = [
  { width: 26 }, // Run date
  { width: 8 }, // Screen
  { width: 44 }, // Question
  { width: 44 }, // Option
  { width: 10 }, // Count
  { width: 12 }, // Percentage
  { width: 15 }, // Multiple picks
];

/**
 * One row per option — the CSV's grain, so the two files pivot the same way.
 *
 * The writer is imported here rather than at the top of the module so it is
 * split out of the console's main bundle: nobody who never exports pays for
 * it, and `packages/web` carries no other dependency of that weight.
 */
export async function resultsToXlsx(source: ResultsSource): Promise<Blob> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');

  const header = [...RESULT_HEADERS, MULTI_SELECT_HEADER].map((value) => ({
    value,
    type: String,
    fontWeight: 'bold' as const,
  }));

  const rows = resultRows(source).map(({ runDate, screenNumber, question, option }) => [
    { value: runDate, type: String },
    { value: screenNumber, type: Number },
    { value: question.text, type: String },
    { value: option.text, type: String },
    { value: option.count, type: Number },
    { value: option.percentage, type: Number },
    question.multiSelect ? { value: MULTI_SELECT_YES, type: String } : null,
  ]);

  return writeXlsxFile([header, ...rows], {
    sheet: 'Results',
    columns: COLUMNS,
    // So the headers stay put when a long poll is scrolled.
    stickyRowsCount: 1,
  }).toBlob();
}
